/** Prüfaufgaben, Erinnerungen, Kalenderabo und Terminanfragen. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'
import type { Pruefart } from './typen'

export type Aufgabenstatus = 'offen' | 'eingeplant' | 'erledigt' | 'entfallen'

export const AUFGABENSTATUSTEXT: Record<Aufgabenstatus, string> = {
  offen: 'offen',
  eingeplant: 'eingeplant',
  erledigt: 'erledigt',
  entfallen: 'entfallen',
}

export type Aufgabe = {
  id: string
  spielplatz_id: string
  pruefart: Pruefart
  faellig_am: string
  intervall_monate: number | null
  status: Aufgabenstatus
  zugewiesen_an_id: string | null
  erinnerung_gesendet_am: string | null
  erledigt_durch_id: string | null
  bemerkung: string | null
  spielplatz_name: string
  kunde_id: string
  kunde_name: string
  zugewiesen_an_name: string | null
}

export type Kalenderabo = { adresse: string; hinweis: string }
export type Erinnerungslauf = { verschickt: number; empfaenger: string[] }

export function useAufgaben(nurOffene = true) {
  return useQuery({
    queryKey: ['aufgaben', nurOffene],
    queryFn: ({ signal }) =>
      api.holen<Aufgabe[]>(`/api/aufgaben?nur_offene=${nurOffene}`, signal),
  })
}

export function useAufgabeAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, daten }: { id: string; daten: Partial<Aufgabe> }) =>
      api.aendern<Aufgabe>(`/api/aufgaben/${id}`, daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['aufgaben'] }),
  })
}

export function useErinnerungenVersenden() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: () => api.senden<Erinnerungslauf>('/api/aufgaben/erinnerungen'),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['aufgaben'] }),
  })
}

export function useKalenderabo() {
  return useMutation({
    mutationFn: () => api.senden<Kalenderabo>('/api/kalenderabo'),
  })
}

export type Kalenderabgleich = {
  angelegt: number
  geaendert: number
  entfernt: number
  unveraendert: number
}

export function useKalenderabgleich() {
  return useMutation({
    mutationFn: () =>
      api.senden<Kalenderabgleich>('/api/aufgaben/kalender/abgleich'),
  })
}

// ── Terminanfragen ───────────────────────────────────────────────

export type Anfragestatus =
  | 'unbestaetigt'
  | 'bestaetigt'
  | 'zugesagt'
  | 'abgelehnt'
  | 'zurueckgezogen'

export type Zeitfenster = 'vormittag' | 'nachmittag' | 'ganztags'

export const ZEITFENSTERTEXT: Record<Zeitfenster, string> = {
  vormittag: 'vormittags',
  nachmittag: 'nachmittags',
  ganztags: 'ganztägig',
}

export type Terminanfrage = {
  id: string
  status: Anfragestatus
  einrichtung: string
  ansprechpartner: string
  email: string
  telefon: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  pruefart: Pruefart | null
  anzahl_geraete: number | null
  wunschdatum: string | null
  zeitfenster: Zeitfenster | null
  nachricht: string | null
  bestaetigt_am: string | null
  beantwortet_am: string | null
  zugesagt_am: string | null
  antwort: string | null
  erstellt_am: string
}

export function useTerminanfragen(nurOffene = true) {
  return useQuery({
    queryKey: ['terminanfragen', nurOffene],
    queryFn: ({ signal }) =>
      api.holen<Terminanfrage[]>(`/api/terminanfragen?nur_offene=${nurOffene}`, signal),
  })
}

export function useAnfrageBeantworten() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      art,
      daten,
    }: {
      id: string
      art: 'zusagen' | 'ablehnen'
      daten: { zugesagt_am?: string | null; antwort?: string | null }
    }) => api.senden<Terminanfrage>(`/api/terminanfragen/${id}/${art}`, daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['terminanfragen'] }),
  })
}

/**
 * Eine Prüfung einplanen.
 *
 * Bisher entstand eine Aufgabe ausschließlich beim Abschluss einer Prüfung.
 * Ein Platz, der noch nie geprüft wurde, blieb damit unter „Fällig“
 * unsichtbar — und das ist genau der Platz, an den man erinnert werden
 * muss.
 */
export function useAufgabeAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      spielplatz_id: string
      pruefart: string
      faellig_am: string
      intervall_monate?: number | null
      zugewiesen_an_id?: string | null
      bemerkung?: string | null
    }) => api.senden<Aufgabe>('/api/aufgaben', daten),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: ['aufgaben'] })
      void speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
    },
  })
}
